// Copyright (c) Docugami, Inc. All rights reserved.

import { ChunkInstance } from "networking/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppDispatch } from "store";
import { IDocumentDgml, useDocumentDgml } from "./documentDgml";
import { getChunkLabel, hasSemanticLabel } from "./LabelChunk";
import { chunkInstanceIsBound } from "./ChunkLinking";
import { getNumberOfChunkSuggestionsForDemoMode } from "utils/localStorageHelpers";
import { useFeatureFlag } from "./FeatureFlags";
import { FeatureFlags } from "store/featureFlags/types";
import { useGetMiddleOutSuggestionsQuery } from "store/reports/reportsSlice";
import { useAppDispatch, useAppSelector } from "./redux";
import { selectIsReportSchemaLoaded, selectReportDocumentId, selectReportId } from "store/reports/selectors";

export interface ISuggestedChunk {
    chunkInstance: ChunkInstance;
    label: string;
}

export interface ISuggestedChunks {
    suggestedChunks: ISuggestedChunk[];
    panelOpen?: boolean;
    flipPanel: () => void;
}

function makeSuggestedChunks(suggestedSpanIds: string[], dgml: IDocumentDgml, dispatch: AppDispatch): ISuggestedChunk[] {
    const suggestions: ISuggestedChunk[] = [];
    let index = 0;
    while (suggestions.length < maxSuggestions && index < suggestedSpanIds.length) {
        const spanId = suggestedSpanIds[index];
        const chunk = dgml.getInstanceById(spanId);
        if (!!chunk && hasSemanticLabel(chunk) && !dispatch(chunkInstanceIsBound(chunk))) {
            suggestions.push({
                chunkInstance: chunk,
                label: dispatch(getChunkLabel(chunk))
            })
        }

        ++index;
    }

    return suggestions;
}

function makeSuggestedChunksForAddAllDemo(suggestedSpanIds: string[], dgml: IDocumentDgml, dispatch: AppDispatch): ISuggestedChunk[] {
    /*
    This is a special mode of chunk suggestions where we will allow the user to add all chunks to the report.
    To make this somewhat sane do the following:
        1. Build the base list of suggestions as we normally would
        2. Only allow a single suggestion per suggested label
        3. Limit to number of suggestions with a mix of small and large chunks
        4. Remove suggestions that are already in the report (this is the last step to avoid a refilling list of suggestions)
    */

    const maxSuggestionForAddAllDemoMode = getNumberOfChunkSuggestionsForDemoMode();

    // 1. Base list of suggestions
    let suggestionsWithSemanticLabels = suggestedSpanIds
        .map(dgml.getInstanceById)
        .filter(x => !!x && hasSemanticLabel(x))
        .map<ISuggestedChunk>(x => ({
            chunkInstance: x!,
            label: dispatch(getChunkLabel(x!))
        }))
        .filter(x => !!x.label);

    // 2. Only allow a single suggestion per label
    let i = 0;
    const labels: Set<string> = new Set();
    while (i < suggestionsWithSemanticLabels.length) {
        if (labels.has(suggestionsWithSemanticLabels[i].label)) {
            suggestionsWithSemanticLabels.splice(i, 1);
            continue;
        }
        labels.add(suggestionsWithSemanticLabels[i].label);
        i++;
    }

    // 3. If we have more suggestions than we want to allow, create a good mix of small and large chunks to return
    if (suggestionsWithSemanticLabels.length > maxSuggestionForAddAllDemoMode) {
        const smallChunks = suggestionsWithSemanticLabels.filter(x => !!x && x.chunkInstance.tokenRange && x.chunkInstance.tokenRange.toTok - x.chunkInstance.tokenRange.fromTok < 20);
        const largerChunks = suggestionsWithSemanticLabels.filter(x => !!x && x.chunkInstance.tokenRange && x.chunkInstance.tokenRange.toTok - x.chunkInstance.tokenRange.fromTok >= 20);

        // take at most 70% small chunks
        const numSmallTaken = Math.min(Math.round(maxSuggestionForAddAllDemoMode * 0.7), smallChunks.length);
        const numLargerTaken = Math.min(maxSuggestionForAddAllDemoMode - numSmallTaken, largerChunks.length);

        suggestionsWithSemanticLabels = [...smallChunks.slice(0, numSmallTaken), ...largerChunks.slice(0, numLargerTaken)];
    }

    // 4. Remove suggestions that are already bound into the report
    return suggestionsWithSemanticLabels
        .filter(x => !!x && !dispatch(chunkInstanceIsBound(x.chunkInstance)));
}

const maxSuggestions = 15;

export function useWorkPathSuggestedChunks(): ISuggestedChunks {
    const reportId = useAppSelector(selectReportId);
    const folderId = useAppSelector(selectReportDocumentId);
    const isSchemaLoaded = useAppSelector(selectIsReportSchemaLoaded)

    const [suggestedItemsOpen, setSuggestedItemsOpen] = useState<boolean | undefined>(undefined);
    const dgml = useDocumentDgml();
    const isAddAllEnabled = useFeatureFlag(FeatureFlags.DemoFeatures);
    const skip = !isSchemaLoaded || !dgml.isInitialized();
    const { data: suggestedSpanIds } = useGetMiddleOutSuggestionsQuery({ folderId: folderId }, { skip: skip })
    const dispatch = useAppDispatch();

    const suggestedChunks = useMemo(() => {
        if (!suggestedSpanIds || !dgml.isInitialized()) {
            return [];
        }

        if (isAddAllEnabled) {
            return makeSuggestedChunksForAddAllDemo(suggestedSpanIds, dgml, dispatch);
        } else {
            return makeSuggestedChunks(suggestedSpanIds, dgml, dispatch);
        }
    },
        [suggestedSpanIds, dgml, isAddAllEnabled, dispatch]);

    useEffect(() => {
        if (!suggestedSpanIds || !dgml.isInitialized()) {
            setSuggestedItemsOpen(undefined);
        }
        else if (suggestedItemsOpen === undefined) {
            setSuggestedItemsOpen(false);
        }
    }, [suggestedSpanIds, dgml, reportId, suggestedItemsOpen])

    const flipPanelCallback = useCallback(() => {
        setSuggestedItemsOpen(!suggestedItemsOpen)
    }, [suggestedItemsOpen])

    return {
        suggestedChunks: suggestedChunks,
        panelOpen: suggestedItemsOpen,
        flipPanel: flipPanelCallback
    };
}
